import { useMemo, useState, type ChangeEvent } from 'react'
import { Search } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage, Input } from '@/shared/ui/primitives'
import { cn } from '@/shared/ui/cn'
import type { CreateOrgType, Member } from '@/shared/domain'

type AssigneeGridProps = {
  members: Member[]
  orgType: CreateOrgType
  currentUserId: string
  selectedAssigneeId: string | null
  onSelect: (id: string | null) => void
}

const SEARCH_THRESHOLD = 8

function getInitials(member: Member): string {
  if (member.firstName && member.lastName) {
    return `${member.firstName[0]}${member.lastName[0]}`.toUpperCase()
  }
  const name = member.fullName ?? ''
  return name.slice(0, 2).toUpperCase()
}

export function AssigneeGrid({
  members,
  orgType,
  currentUserId,
  selectedAssigneeId,
  onSelect,
}: AssigneeGridProps) {
  const [searchQuery, setSearchQuery] = useState('')

  const visibleMembers = useMemo(() => {
    const excludedRole = orgType === 'outfitter' ? 'client' : 'guest'
    const filtered = members.filter((member) => member.role !== excludedRole)
    return filtered.sort((a, b) => {
      if (a.id === currentUserId) return -1
      if (b.id === currentUserId) return 1
      return a.fullName.localeCompare(b.fullName)
    })
  }, [members, orgType, currentUserId])

  const showSearch = visibleMembers.length > SEARCH_THRESHOLD

  const filteredMembers = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    if (!query) return visibleMembers
    return visibleMembers.filter((member) =>
      member.fullName.toLowerCase().includes(query),
    )
  }, [visibleMembers, searchQuery])

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value)
  }

  return (
    <div className="flex flex-col gap-2">
      {showSearch ? (
        <div className="relative">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            type="search"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Search members..."
            aria-label="Search members"
            className="pl-9"
          />
        </div>
      ) : null}

      <div role="radiogroup" className="grid grid-cols-2 gap-2">
        <button
          type="button"
          role="radio"
          aria-checked={selectedAssigneeId === null}
          onClick={() => onSelect(null)}
          className={cn(
            'flex items-center gap-2 rounded-md border p-2 text-left text-sm transition-colors',
            selectedAssigneeId === null
              ? 'border-primary bg-primary/10 ring-1 ring-primary'
              : 'border-border hover:bg-muted/40',
          )}
        >
          <Avatar>
            <AvatarFallback>SE</AvatarFallback>
          </Avatar>
          <span>Self (default)</span>
        </button>

        {filteredMembers.map((member) => {
          const selected = selectedAssigneeId === member.id
          return (
            <button
              key={member.id}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onSelect(member.id)}
              className={cn(
                'flex items-center gap-2 rounded-md border p-2 text-left text-sm transition-colors',
                selected
                  ? 'border-primary bg-primary/10 ring-1 ring-primary'
                  : 'border-border hover:bg-muted/40',
              )}
            >
              <Avatar>
                {member.avatarUrl ? (
                  <AvatarImage src={member.avatarUrl} alt={member.fullName} />
                ) : null}
                <AvatarFallback>{getInitials(member)}</AvatarFallback>
              </Avatar>
              <span className="truncate">{member.fullName}</span>
            </button>
          )
        })}
      </div>

      {showSearch && filteredMembers.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No members match &ldquo;{searchQuery}&rdquo;.
        </p>
      ) : null}
    </div>
  )
}
